import chunk from "lodash-es/chunk";
import type React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  useBacktests,
  useExecutions,
  useJobPolling,
  useJobs,
  useOptimizations,
} from "@src/hooks/redux";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type { JobPollingState } from "@src/services/state/layoutReducer";
import { EXECUTION_POLL_DELAY_MS, EXECUTION_POLL_MAX_ATTEMPTS } from "@src/utils/constants";
import { delay } from "@src/utils/dateUtils";
import { getPausedTrials } from "@src/utils/jobPauseResume";
import { getEntityLink } from "@src/utils/routing";
import type { ApiJob, ApiOptimizationJobResult } from "@tradingdroid/frontends-types";

// Statuses that indicate a job is still active and should be polled
// Note: "running" is included here but live executions are filtered out separately
// since they're expected to run indefinitely. "paused" waits for a resume, which tracks it again.
const ACTIVE_JOB_STATUSES: JobPollingState["status"][] = ["queued", "claimed", "running"];
const SCAN_JOB_TYPE = "scan";
const ACTIVE_JOB_PAGE_LIMIT = 20;
const ACTIVE_STATUS_CLAUSE = `(${ACTIVE_JOB_STATUSES.map((status) => `status_name.EQ:'${status}'`).join(" OR ")})`;
// The job table's columns without `result`, which can be large for a scan. If a name here is not a job
// column, the API refuses the scan list request; the poller then finds no scans and leaves tracked scans
// as they are.
const SCAN_POLL_FIELDS =
  "id,job_type_name,strategy_id,config,status_name,priority,worker_id,error_message,exchange_name,symbol_name,period,from_ts,end_ts,description,create_ts,update_ts,claim_ts,start_ts,complete_ts,target_worker_id,metadata";

// Filter out "live" jobs with "running" status (they run indefinitely), and coordinator child
// backtests (Monte Carlo permutation OR CPCV children) — a run spawns hundreds and they must not
// flood startup toasts / polling or appear as standalone jobs.
const isPollableJob = (job: ApiJob): boolean =>
  !(job.job_type_name === "live" && job.status_name === "running") &&
  job.metadata?.origin?.type !== "monte_carlo_child" &&
  job.metadata?.origin?.type !== "walk_forward_cpcv_child";

const fetchActiveJobRows = async (scan: boolean, ids?: number[]): Promise<ApiJob[] | null> => {
  try {
    const query = `${ACTIVE_STATUS_CLAUSE} AND job_type_name.${scan ? "EQ" : "NE"}:'${SCAN_JOB_TYPE}'`;
    const response = await tdApi.getJobs({
      q: ids ? `${query} AND id.in:'${ids.join(",")}'` : query,
      limit: ids?.length ?? ACTIVE_JOB_PAGE_LIMIT,
      sort_by: "id:DESC",
      ...(scan && { fields: SCAN_POLL_FIELDS }),
    });
    return (response?.items || []).filter(isPollableJob);
  } catch (error) {
    console.error(
      `[JobPolling] Error fetching active ${scan ? "scan" : "non-scan"} jobs${ids ? " by id" : ""}:`,
      error,
    );
    return null;
  }
};

// Reads `ids` in chunks of at most ACTIVE_JOB_PAGE_LIMIT. When a chunk fails the result is null and
// the rows of the chunks that loaded are dropped too, so the caller keeps this kind's off-page jobs
// unchanged until a later poll.
const fetchActiveJobsById = async (scan: boolean, ids: number[]): Promise<ApiJob[] | null> => {
  const rows = await Promise.all(
    chunk(ids, ACTIVE_JOB_PAGE_LIMIT).map((idChunk) => fetchActiveJobRows(scan, idChunk)),
  );
  if (rows.some((result) => result === null)) return null;
  return rows.flatMap((result) => result ?? []);
};

export const JobPollingManager: React.FC = () => {
  const { showToast } = useToastActions();
  const navigate = useNavigate();
  const { upsertBacktest } = useBacktests();
  const { upsertOptimization } = useOptimizations();
  const { upsertExecution } = useExecutions();
  const { upsertJob, mergeJob } = useJobs();

  const {
    activeJobs,
    shouldSyncJobs,
    trackActiveJob,
    updateActiveJob,
    untrackActiveJob,
    resetJobSyncFlag,
  } = useJobPolling();

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCounterRef = useRef<number>(0);
  const pollInFlightRef = useRef(false);

  // Untracking is deferred so the completion toast lands before the job leaves the list.
  // The timers live here rather than in the polling effect: that effect re-runs on every
  // activeJobs change, and cleaning up there would cancel a pending untrack and leave the
  // job polling forever. Unmount clears them so none fire against a gone component.
  // Keyed by job id so two overlapping polls seeing the same terminal job schedule one untrack,
  // not two.
  const untrackTimersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const scheduleUntrack = useCallback(
    (jobId: number) => {
      if (untrackTimersRef.current.has(jobId)) return;
      const timer = setTimeout(() => {
        untrackTimersRef.current.delete(jobId);
        untrackActiveJob(jobId);
      }, 500);
      untrackTimersRef.current.set(jobId, timer);
    },
    [untrackActiveJob],
  );
  useEffect(() => {
    const timers = untrackTimersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  // Use a ref to stabilize functions in onJobFinished. Written after commit, never
  // during render, so a re-render never mutates state another render is reading.
  const latestRef = useRef({
    showToast,
    navigate,
    upsertBacktest,
    upsertOptimization,
    upsertExecution,
    upsertJob,
    mergeJob,
  });
  useLayoutEffect(() => {
    latestRef.current = {
      showToast,
      navigate,
      upsertBacktest,
      upsertOptimization,
      upsertExecution,
      upsertJob,
      mergeJob,
    };
  });

  const showSuccessEntityToast = useCallback((entityType: string, entityId: number) => {
    // For live executions, show "Execution #ID started" instead of "live completed"
    const isLiveExecution = entityType === "live";
    const displayType = isLiveExecution ? "Execution" : entityType;
    const message = isLiveExecution
      ? `Execution #${entityId} started`
      : `${displayType} #${entityId} completed successfully!`;

    latestRef.current.showToast({
      message,
      type: "success",
      timeout: 10000,
      actions: entityId
        ? [
            {
              label: `View ${displayType}`,
              onClick: () => latestRef.current.navigate(getEntityLink(entityType, entityId)),
            },
          ]
        : undefined,
    });
  }, []);

  // Poll for execution data with retry logic
  const pollForExecutionData = useCallback(
    async (jobId: number, maxAttempts = EXECUTION_POLL_MAX_ATTEMPTS): Promise<ApiJob | null> => {
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        await delay(EXECUTION_POLL_DELAY_MS);
        try {
          const jobWithExecution = await tdApi.getJob(jobId, { embed: "execution" });
          if (jobWithExecution?.execution?.id) {
            console.log(`[JobPolling] Got execution data for job #${jobId} on attempt ${attempt}`);
            return jobWithExecution;
          }
          console.log(
            `[JobPolling] Attempt ${attempt}/${maxAttempts}: Execution not ready for job #${jobId}`,
          );
        } catch (error) {
          console.error(`[JobPolling] Error polling for execution (attempt ${attempt}):`, error);
        }
      }
      return null;
    },
    [],
  );

  // Function to handle job completion and show notifications

  const onJobFinished = useCallback(
    async (job: ApiJob) => {
      // Handle completion/status notifications based on job type and status
      console.log("[JobPolling] Job finished:", job);

      // Special handling for live executions
      if (job.job_type_name === "live") {
        if (job.status_name !== "running") return;

        // Validate execution has actual data (not just empty object)
        if (!job.execution?.id) {
          console.log(
            `[JobPolling] Live job #${job.id} running but execution not ready yet, polling...`,
          );

          // Poll until execution is available
          const jobWithExecution = await pollForExecutionData(job.id);

          if (!jobWithExecution?.execution?.id) {
            console.error(
              `[JobPolling] Live job #${job.id} execution never became available after polling`,
              job,
            );
            return;
          }

          // Use the fetched job with execution data
          latestRef.current.upsertJob(jobWithExecution);
          latestRef.current.upsertExecution(jobWithExecution.execution);
          showSuccessEntityToast(job.job_type_name, jobWithExecution.execution.id);
          return;
        }

        // upsert execution and notify these with "running" status
        latestRef.current.upsertExecution(job.execution);
        showSuccessEntityToast(job.job_type_name, job.execution.id);
        return;
      }

      // backtest and optimization jobs can be treated similarly
      if (job.status_name === "completed") {
        // Extract entity ID based on job type and embedded data
        let entityId: number | undefined;

        if (job.job_type_name === "backtest") {
          // Add backtest to Redux state immediately when job completes
          if (job.backtest) {
            entityId = job.backtest.id;
            latestRef.current.upsertBacktest(job.backtest);
          }
        } else if (job.job_type_name === "optimization") {
          // Extract study_id from job result data
          const optimizationResult = job.result as ApiOptimizationJobResult | undefined;
          const studyId = optimizationResult?.data?.study_id;

          if (!studyId) {
            // Backend error - optimization job should always have study_id in result
            console.error(
              `[JobPolling] No study_id in optimization job result for job ${job.id}`,
              job.result,
            );
            latestRef.current.showToast({
              title: "Optimization error",
              message: `Job #${job.id} completed but backend did not return study_id`,
              type: "danger",
              timeout: 15000,
            });
            return; // Abort - can't proceed without study_id
          }

          try {
            // Fetch the study from /v1/studies API with embedded job
            const study = await tdApi.getStudy(studyId, { embed: "job" });
            if (study) {
              // Only mark the entity resolved once the study actually loaded, so a 404 (a study the
              // caller doesn't own, now that studies are owner-scoped) doesn't fire a success toast
              // that deep-links to a page that then 404s.
              entityId = studyId;
              // Enrich study with job metadata (prefer embedded job, fallback to current job)
              const jobData = study.job || job;
              const enrichedStudy = {
                ...study,
                strategy_id: jobData.strategy_id,
                exchange_name: jobData.exchange_name,
                symbol_name: jobData.symbol_name,
                period: jobData.period,
                from_ts: jobData.from_ts,
                end_ts: jobData.end_ts,
                created_ts: jobData.create_ts,
                completed_ts: jobData.complete_ts,
              };
              // Store enriched study in Redux
              latestRef.current.upsertOptimization(enrichedStudy);
            } else {
              console.warn(`[JobPolling] Study ${studyId} not found for job ${job.id}`);
            }
          } catch (error) {
            console.error(
              `[JobPolling] Failed to fetch study ${studyId} for job ${job.id}:`,
              error,
            );
          }
        }

        // Only show toast if we have an entity ID (skip for unhandled job types like "cache")
        if (entityId !== undefined) {
          showSuccessEntityToast(job.job_type_name, entityId);
        } else {
          console.log(
            `[JobPolling] Job #${job.id} (${job.job_type_name}) completed - no entity toast`,
          );
        }
      } else if (job.status_name === "failed") {
        latestRef.current.showToast({
          title: `${job.job_type_name} job failed`,
          message: job.error_message || "Unknown error",
          type: "danger",
          timeout: 15000, // 15 seconds for errors - need time to read
        });
      } else if (job.status_name === "cancelled") {
        latestRef.current.showToast({
          message: `${job.job_type_name} was cancelled`,
          type: "warning",
          timeout: 10000,
        });
      } else if (job.status_name === "paused") {
        const trials = getPausedTrials(job);
        latestRef.current.showToast({
          message:
            trials === undefined
              ? `Optimization #${job.id} paused`
              : `Optimization #${job.id} paused at ${trials} trials`,
          type: "info",
          timeout: 10000,
        });
      }
    },
    [showSuccessEntityToast, pollForExecutionData], // pollForExecutionData is stable (no deps)
  );

  // Fetch active non-scan rows and projected scan rows in parallel.
  // Excludes "live" jobs with "running" status since they run indefinitely
  const fetchActiveJobs = useCallback(async (): Promise<{
    fullRows: ApiJob[] | null;
    scanRows: ApiJob[] | null;
  }> => {
    const [fullRows, scanRows] = await Promise.all([
      fetchActiveJobRows(false),
      fetchActiveJobRows(true),
    ]);
    return { fullRows, scanRows };
  }, []);

  // Function to sync Redux state with API active jobs
  const checkActiveJobs = useCallback(async () => {
    const { fullRows, scanRows } = await fetchActiveJobs();
    const apiActiveJobs = [...(fullRows ?? []), ...(scanRows ?? [])];

    // Add any jobs from API that aren't in Redux
    apiActiveJobs.forEach((apiJob) => {
      const existingJob = activeJobs.find((job) => job.jobId === apiJob.id);
      if (!existingJob) {
        // Add job to Redux (we might not have all metadata, but we can track it)
        trackActiveJob({
          jobId: apiJob.id,
          jobType: apiJob.job_type_name,
          entityType:
            apiJob.job_type_name === "backtest"
              ? "backtest"
              : apiJob.job_type_name === "optimization"
                ? "optimization"
                : apiJob.job_type_name === "live"
                  ? "execution"
                  : // Other job types get no entity link in this path.
                    undefined,
          metadata: {
            title: `${apiJob.job_type_name} Job ${apiJob.id}`,
            description: "Job discovered at app start",
          },
        });
      }
    });

    return apiActiveJobs.length > 0;
  }, [activeJobs, trackActiveJob, fetchActiveJobs]);

  // shouldSyncJobs is now provided by useJobPolling hook

  // Initialize: check for existing active jobs on mount (ONCE)
  useEffect(() => {
    const checkOnMount = async () => {
      const hasJobs = await checkActiveJobs();
      if (!hasJobs) {
        console.log("[JobPolling] No active jobs found on mount - polling disabled");
      }
    };
    checkOnMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, not when checkActiveJobs changes

  // Sync jobs when triggerJobSync is dispatched (when user creates new job)
  useEffect(() => {
    if (shouldSyncJobs) {
      console.log("[JobPolling] New job created - starting polling");
      checkActiveJobs();
      // Reset the flag after syncing
      resetJobSyncFlag();
    }
  }, [shouldSyncJobs, resetJobSyncFlag, checkActiveJobs]);

  // Single polling instance manager - ensures only ONE polling interval runs
  useEffect(() => {
    // Exclude "live" jobs with "running" status from active polling
    const hasActiveJobs = activeJobs.some(
      (job) =>
        ACTIVE_JOB_STATUSES.includes(job.status) &&
        !(job.jobType === "live" && job.status === "running"),
    );

    const pollAllActiveJobs = async () => {
      // Exclude "live" jobs with "running" status - they're expected to run indefinitely
      const runningJobs = activeJobs.filter(
        (job) =>
          ACTIVE_JOB_STATUSES.includes(job.status) &&
          !(job.jobType === "live" && job.status === "running"),
      );

      if (runningJobs.length === 0) {
        return;
      }

      // A poll can outlast the 3s interval. Without this, the next tick starts a second pass over
      // the same jobs, which double-fires completion toasts and upserts.
      if (pollInFlightRef.current) {
        return;
      }
      pollInFlightRef.current = true;

      // Increment counter
      pollCounterRef.current += 1;
      const currentIteration = pollCounterRef.current;

      // Log every 10 iterations
      if (currentIteration % 10 === 0) {
        console.log(
          `[JobPolling] Iteration #${currentIteration} - Polling ${runningJobs.length} active job(s): ${runningJobs.map((j) => `#${j.jobId} (${j.status})`).join(", ")}`,
        );
      }

      try {
        // BULK POLLING: Fetch full non-scan rows and projected scan rows together.
        const { fullRows, scanRows } = await fetchActiveJobs();

        // Replace full rows and merge scan projections into the Redux jobs list.
        const apiJobsById = new Map<number, ApiJob>();
        const storeRows = (rows: ApiJob[] | null, scan: boolean): void => {
          rows?.forEach((job) => {
            apiJobsById.set(job.id, job);
            if (scan) latestRef.current.mergeJob(job);
            else latestRef.current.upsertJob(job);
          });
        };
        storeRows(fullRows, false);
        storeRows(scanRows, true);

        const missingFullIds: number[] = [];
        const missingScanIds: number[] = [];
        for (const reduxJob of runningJobs) {
          if (apiJobsById.has(reduxJob.jobId)) continue;
          if (reduxJob.jobType === SCAN_JOB_TYPE) {
            if (scanRows !== null) missingScanIds.push(reduxJob.jobId);
          } else if (fullRows !== null) {
            missingFullIds.push(reduxJob.jobId);
          }
        }

        const [fullRowsById, scanRowsById] = await Promise.all([
          fetchActiveJobsById(false, missingFullIds),
          fetchActiveJobsById(true, missingScanIds),
        ]);
        storeRows(fullRowsById, false);
        storeRows(scanRowsById, true);

        // Compare Redux jobs with API jobs and update statuses
        for (const reduxJob of runningJobs) {
          // Keep tracking unchanged until a later poll if this job kind's list request failed.
          if ((reduxJob.jobType === SCAN_JOB_TYPE ? scanRows : fullRows) === null) continue;
          // A job its page did not return also stays unchanged when its kind's by-id read failed;
          // page rows of that kind are still compared.
          if (
            !apiJobsById.has(reduxJob.jobId) &&
            (reduxJob.jobType === SCAN_JOB_TYPE ? scanRowsById : fullRowsById) === null
          )
            continue;
          const apiJob = apiJobsById.get(reduxJob.jobId);

          if (!apiJob) {
            // Job completed/failed - fetch individual job to check final status
            try {
              // Wait briefly for backend to resolve embeds
              await delay(1500);

              // Use appropriate embed based on job type
              const embedParam =
                reduxJob.jobType === "backtest"
                  ? "backtest"
                  : reduxJob.jobType === "live"
                    ? "execution"
                    : undefined;

              const finalJob = await tdApi.getJob(
                reduxJob.jobId,
                embedParam ? { embed: embedParam } : {},
              );
              if (finalJob) {
                // Upsert completed/failed job to Redux
                latestRef.current.upsertJob(finalJob);

                // Handle completion notification
                onJobFinished(finalJob);
              }
            } catch (error) {
              console.error(
                `[JobPolling] Error fetching final job status for ${reduxJob.jobId}:`,
                error,
              );
            }

            // Remove from tracking
            untrackActiveJob(reduxJob.jobId);
            continue;
          }

          const currentStatus = apiJob.status_name as JobPollingState["status"];

          if (currentStatus !== reduxJob.status) {
            // Status changed - update Redux
            // A job row names no backtest or execution id; only an optimization's result names its study.
            let entityId: number | undefined;
            if (apiJob.job_type_name === "optimization") {
              const optResult = apiJob.result as ApiOptimizationJobResult | undefined;
              entityId = optResult?.data?.study_id;
            }

            updateActiveJob({
              jobId: reduxJob.jobId,
              status: currentStatus,
              entityId,
            });

            // Handle status change to terminal or running state
            // This path is for jobs still in the active list that changed status
            // Different from the path above where jobs disappeared from the list entirely
            if (
              currentStatus === "completed" ||
              currentStatus === "failed" ||
              currentStatus === "cancelled" ||
              currentStatus === "paused" ||
              (apiJob.job_type_name === "live" && currentStatus === "running")
            ) {
              // For live jobs transitioning to "running", fetch with embed to get execution data
              if (apiJob.job_type_name === "live" && currentStatus === "running") {
                try {
                  await delay(EXECUTION_POLL_DELAY_MS); // Wait for backend to create execution record
                  const jobWithExecution = await tdApi.getJob(apiJob.id, { embed: "execution" });
                  if (jobWithExecution) {
                    latestRef.current.upsertJob(jobWithExecution);
                    onJobFinished(jobWithExecution);
                  }
                } catch (error) {
                  console.error(
                    `[JobPolling] Error fetching live job ${apiJob.id} with execution embed:`,
                    error,
                  );
                }
              } else {
                onJobFinished(apiJob);
              }

              // Remove from polling after showing toast
              // Live executions with "running" status run indefinitely, others completed
              scheduleUntrack(reduxJob.jobId);
            }
          }
        }
      } catch (error) {
        console.error("[JobPolling] Error in bulk job polling:", error);
      } finally {
        pollInFlightRef.current = false;
      }
    };

    // SINGLE POLLING INSTANCE CONTROL
    if (hasActiveJobs && !pollIntervalRef.current) {
      // Start polling - there's only ever one interval running
      const wasPolling = pollCounterRef.current > 0;
      pollCounterRef.current = 0; // Reset counter on new polling session

      if (!wasPolling) {
        console.log(`[JobPolling] Starting polling for ${activeJobs.length} active job(s)`);
      }

      pollIntervalRef.current = setInterval(pollAllActiveJobs, 3000);
      // Run first poll immediately
      pollAllActiveJobs();
    } else if (!hasActiveJobs && pollIntervalRef.current) {
      // Stop polling when no active jobs
      console.log(
        `[JobPolling] All jobs finished - stopping polling after ${pollCounterRef.current} iterations`,
      );
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      pollCounterRef.current = 0; // Reset counter
    }
    // If hasActiveJobs && pollIntervalRef.current exists: Do nothing - polling already running

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [
    activeJobs,
    updateActiveJob,
    untrackActiveJob,
    scheduleUntrack,
    onJobFinished,
    fetchActiveJobs,
  ]);

  return null; // This is a background component
};
